import * as Analytics from "@app/shared/lib/Analytics";
import { VkLoginPayload } from "@features/authV2/lib/sdk.ts";
import { LoginProvider } from "@features/authV2/lib/types.ts";
import { VkOneTapButton } from "@features/authV2/ui/VkOneTapButton.tsx";
import {
	ActionIcon,
	Anchor,
	Box,
	Button,
	Center,
	CloseButton,
	Collapse,
	Divider,
	Grid,
	Group,
	Paper,
	Stack,
	Text,
	TextInput,
	Title
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ContextModalProps } from "@mantine/modals";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { FxIdApplicationPublicDataBuilderVkontakteComVkIdOptions } from "@shared/api/gen";
import { useQueryClient } from "@tanstack/react-query";
import log from "loglevel";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TbArrowLeft } from "react-icons/tb";
import { z } from "zod";

import { useLoginMutation } from "../api/useLoginMutation";
import { socialProviders } from "../config/config.tsx";
import classes from "./LoginViewForContext.module.scss";
import { SocialButton } from "./SocialButton";

const schema = z.object({
	email: z.email({ message: "Неверный формат email" })
});

export interface LoginViewForContextProps {
	turnstileSiteKey: string;
	gameSystemName?: string;
	onAuthenticated?: () => void;
	onAuthenticatedEmailTimeout?: number; // Добавляем проп для таймаута

	// Добавляем обязательные пропсы для ссылок
	tosUrl: string;
	ppUrl: string;
	// Список разрешенных провайдеров
	allowedProviders?: LoginProvider[];
	vkOptions?: FxIdApplicationPublicDataBuilderVkontakteComVkIdOptions | null;
}

/**
 * Компонент отображения формы входа, стилизованный под макет.
 * Реализует логику входа через Email (с Turnstile) и социальные сети.
 */
export const LoginViewForContext = ({ context, id, innerProps }: ContextModalProps<LoginViewForContextProps>) => {
	const {
		turnstileSiteKey,
		gameSystemName,
		onAuthenticated,
		tosUrl,
		ppUrl,
		allowedProviders,
		vkOptions,
		onAuthenticatedEmailTimeout
	} = innerProps;
	const { mutate, isPending } = useLoginMutation();
	const [generalError, setGeneralError] = useState<string | null>(null);
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const [emailLoginStep, setEmailLoginStep] = useState<"enterEmail" | "verifyTurnstile" | "success">("enterEmail");
	const [savedLoginEmail, setSavedLoginEmail] = useState("");
	const turnstileRef = useRef<TurnstileInstance>(null);

	const form = useForm({
		initialValues: { email: "" },
		validate: zod4Resolver(schema),
		validateInputOnChange: true
	});

	// Эффект для автоматического вызова Turnstile challenge
	useEffect(() => {
		if (emailLoginStep === "verifyTurnstile" && turnstileRef.current) {
			log.debug("Turnstile component is visible, executing challenge now");
			turnstileRef.current.execute();
		}
	}, [emailLoginStep]);

	// Обработчик успешной аутентификации через соц. сети
	const handleSocialAuthenticationSuccess = (provider: LoginProvider) => {
		log.info(`Login successful with provider: ${provider}`);
		Analytics.site.login({ method: provider });
		queryClient.clear();
		onAuthenticated?.();
		context.closeModal(id);
	};

	// Обработчик успешного прохождения Turnstile
	const onTurnstileSuccess = useCallback(
		(token: string) => {
			log.debug(`Turnstile challenge passed for email: ${savedLoginEmail}. Proceeding with sending magic link.`);
			setGeneralError(null);

			mutate(
				{ provider: "email", email: savedLoginEmail, captchaToken: token, gameSystemName },
				{
					onSuccess: () => {
						log.info(`Magic link sent successfully to: ${savedLoginEmail}`);
						Analytics.site.login({ method: "email" });
						// Вызываем onAuthenticated с учетом таймаута
						if (onAuthenticated) {
							if (onAuthenticatedEmailTimeout && onAuthenticatedEmailTimeout > 0) {
								log.debug(`Delaying onAuthenticated call by ${onAuthenticatedEmailTimeout}ms`);
								setTimeout(() => {
									log.debug("Executing delayed onAuthenticated");
									onAuthenticated();
								}, onAuthenticatedEmailTimeout);
							} else {
								log.debug("Executing onAuthenticated immediately");
								onAuthenticated();
							}
						}
						setEmailLoginStep("success");
					},
					onError: (error) => {
						log.error("Login mutation failed after Turnstile success", error);
						setGeneralError(t("sign_in_modal.errors.general_error", "Failed to login. Try again"));
						setEmailLoginStep("enterEmail");
						turnstileRef.current?.reset();
					}
				}
			);
		},
		[mutate, savedLoginEmail, gameSystemName, t, onAuthenticated, onAuthenticatedEmailTimeout]
	);

	// Обработчик сабмита формы с email
	const handleEmailSubmit = () => {
		log.debug("Email form submitted, switching to Turnstile view");
		if (form.validate().hasErrors) return;
		setGeneralError(null);
		setSavedLoginEmail(form.values.email);
		setEmailLoginStep("verifyTurnstile");
	};

	// Обработчик клика по кнопке соц. сети
	const handleSocialLogin = (provider: Exclude<LoginProvider, "vk">) => {
		setGeneralError(null);
		mutate(
			{ provider, gameSystemName },
			{
				onSuccess: () => handleSocialAuthenticationSuccess(provider),
				onError: (error) => {
					log.error(`Social login mutation failed for provider: ${provider}`, error);
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					if ((error as any).code === "auth/popup-closed-by-user") {
						setGeneralError(t("sign_in_modal.errors.popup_closed", "Popup was closed"));
					} else {
						setGeneralError(t("sign_in_modal.errors.general_error", "Failed to login. Try again"));
					}
				}
			}
		);
	};

	// Обработчик успешного логина через VK One-Tap
	const handleVkLoginSuccess = (data: VkLoginPayload) => {
		log.info("Handling VK login success, calling mutation");
		setGeneralError(null);
		mutate(
			{ provider: "vk", vkPayload: data, gameSystemName },
			{
				onSuccess: () => handleSocialAuthenticationSuccess("vk"),
				onError: (error) => {
					log.error("VK login mutation failed", error);
					setGeneralError(t("sign_in_modal.errors.general_error", "Failed to login. Try again"));
				}
			}
		);
	};

	// Отделяем провайдер VK от остальных
	const vkProvider = allowedProviders?.find((p) => p === "vk");
	// Фильтруем `socialProviders` на основе `allowedProviders` из пропсов.
	// Если `allowedProviders` не предоставлен, не показываем никаких социальных кнопок.
	const otherSocialProviders = socialProviders.filter(
		(p) => p.provider !== "vk" && allowedProviders?.includes(p.provider)
	);

	return (
		<Paper className={classes.authCard} shadow="xl" p="xl" radius="lg" style={{ position: "relative" }}>
			{/* Добавляем кнопку закрытия в правый верхний угол, если это не экран успеха */}
			{emailLoginStep !== "success" && (
				<CloseButton
					onClick={() => context.closeModal(id)}
					title="Close"
					aria-label="Close"
					style={{ position: "absolute", top: 15, right: 15 }}
				/>
			)}
			<Box className={classes.wrapper}>
				{emailLoginStep === "success" ? (
					// Экран успешной отправки ссылки
					<Center style={{ flex: 1 }}>
						<Stack ta="center" gap="md">
							<Title order={3}>
								{t("sign_in_modal.success_alert.title", "Check mail for login link")}
							</Title>
							<Text size="sm" c="dimmed">
								<Trans
									t={t}
									i18nKey="sign_in_modal.success_alert.text"
									values={{ email: savedLoginEmail }}
									defaults="Magic login link sent to <1>{{email}}</1>"
									components={[<b key="email" />]}
								/>
							</Text>
							<Button onClick={() => context.closeModal(id)} mt="lg" fullWidth>
								{t("common.buttons.close", "Close")}
							</Button>
						</Stack>
					</Center>
				) : (
					// Основное содержимое формы
					<Stack gap="xl" justify="center" style={{ flex: 1 }}>
						<Stack ta="center" gap={4}>
							<Title order={2} className={classes.title}>
								{t("sign_in_modal.title", "Sing in")}
							</Title>
							{/*<Text size="sm" c="dimmed">*/}
							{/*	{t("sign_in_modal.subtitle", "Sing in")}*/}
							{/*</Text>*/}
						</Stack>

						{/* Шаг ввода Email и социальные сети */}
						{emailLoginStep === "enterEmail" && (
							<Stack gap="lg">
								<form onSubmit={form.onSubmit(handleEmailSubmit)}>
									<Stack>
										<TextInput
											required
											label={t("common.labels.email", "Email")}
											placeholder="hello@example.com"
											disabled={isPending}
											{...form.getInputProps("email")}
										/>
										<Button type="submit" size="md" loading={isPending} fullWidth>
											{t("sign_in_modal.buttons.continue_with_email", "Continue with email")}
										</Button>
									</Stack>
								</form>
								{/* Показываем разделитель и кнопки только если они есть */}
								{/* Показываем разделитель и кнопки только если они есть */}
								{(otherSocialProviders.length > 0 || vkProvider) && (
									<>
										<Divider
											label={t("sign_in_modal.divider_text", "Other ways to sign in")}
											labelPosition="center"
										/>
										{/* Рендерим кнопку VK если она разрешена и есть AppId */}
										{vkProvider && vkOptions && (
											<VkOneTapButton
												appId={vkOptions.ClientId}
												scope={vkOptions.Scope}
												showOkLogin={vkOptions.ShowOkLogin}
												showMailRuLogin={vkOptions.ShowMailRuLogin}
												onSuccess={handleVkLoginSuccess}
												onError={(err) => setGeneralError(err.message)}
											/>
										)}
										<Grid>
											{otherSocialProviders.map((config) => (
												<Grid.Col span={6} key={config.provider}>
													<SocialButton
														{...config}
														onClick={() =>
															handleSocialLogin(
																config.provider as Exclude<
																	LoginProvider,
																	"vk" | "email"
																>
															)
														}
														loading={isPending}
													/>
												</Grid.Col>
											))}
										</Grid>
									</>
								)}
							</Stack>
						)}

						{/* Шаг подтверждения Turnstile */}
						{emailLoginStep === "verifyTurnstile" && (
							<Stack gap="md">
								<Group
									gap="xs"
									onClick={() => setEmailLoginStep("enterEmail")}
									style={{ cursor: "pointer" }}
								>
									<ActionIcon variant="transparent" c="dimmed">
										<TbArrowLeft />
									</ActionIcon>
									<Text size="sm" c="dimmed">
										{t("sign_in_modal.buttons.back_to_email", "Back to email")}
									</Text>
								</Group>

								<Text fw={500}>{t("sign_in_modal.turnstile.title", "Robot check")}</Text>

								<Center>
									<Turnstile
										ref={turnstileRef}
										siteKey={turnstileSiteKey}
										onSuccess={onTurnstileSuccess}
										onError={() => {
											log.error("Turnstile challenge failed");
											setGeneralError(
												t(
													"sign_in_modal.errors.turnstile_failed",
													"Robot check error. Try again."
												)
											);
											setEmailLoginStep("enterEmail");
										}}
									/>
								</Center>
							</Stack>
						)}

						{/* Блок для отображения общих ошибок */}
						<Collapse in={!!generalError}>
							<Text c="red" size="sm" ta="center">
								{generalError}
							</Text>
						</Collapse>

						{/* Футер с ссылками на документы */}
						<Text size="xs" c="dimmed" ta="center" mt="auto">
							<Trans
								t={t}
								i18nKey="sign_in_modal.footer_text"
								defaults="By using this form you accept our <0>Terms of Service</0> and <1>Privacy policy</1>."
								components={[
									<Anchor href={tosUrl} key="tos" target="_blank" fz="xs" />,
									<Anchor href={ppUrl} key="pp" target="_blank" fz="xs" />
								]}
							/>
						</Text>
					</Stack>
				)}
			</Box>
		</Paper>
	);
};
